// Shared pace state on the device. The Worker is the authority (GET /api/sync → shared); this
// module only (a) overlays today's local study onto the last pulled state, and (b) estimates a
// state when none was ever pulled (first start offline, or the API not running in dev).
import type { CardRow, SharedState } from '@valyrio/engine/api';
import type { EarlyOpen, PaceConfig, PaceRise } from '@valyrio/engine/types';
import { DEFAULT_STAIRCASE } from '@valyrio/engine/pace-defaults';
import { lessonsOpen, localDate, newPerDay, releaseTarget, unlockDateOf } from './engine';
import { STEADY_DAYS, type StoredCard } from './fsrs';

/** Mirrors api/src/frontier.ts defaultPace() and scripts/src/lib/course-plan.ts DEFAULT_PACE. */
export const FALLBACK_PACE: PaceConfig = {
  startDate: '2026-10-10',
  newPerDayFirstMonth: 5,
  newPerDayAfter: 9,
  firstMonthDays: 30,
  lessonsPerWeek: 1,
  pauseIfOverdueAbove: 150,
  timezone: 'Europe/Helsinki',
  goalDate: '2027-10-31',
  // the rising word pace (Axel, 2026-10-14; engine/src/pace-defaults.ts)
  staircase: { ...DEFAULT_STAIRCASE },
};

/** Words the frontier would have released by `now` (no pauses known): the pace's rate summed (releaseTarget). */
export function estimateReleased(pace: PaceConfig, now: Date, total: number): number {
  return Math.min(total, releaseTarget(now, pace));
}

export function estimateShared(pace: PaceConfig, now: Date, totalWords: number, meId: string): SharedState {
  return {
    // only lesson 1 opens by the calendar alone; the rest wait for passes (lib/course.ts
    // withLocalPasses folds in mine; the server knows both)
    lessonsUnlocked: lessonsOpen(now, pace, 0),
    nextUnlock: unlockDateOf(lessonsOpen(now, pace, 0) + 1, pace),
    lessonsPassed: {},
    wordsReleased: estimateReleased(pace, now, totalWords),
    newPerDay: newPerDay(now, pace),
    newWordsPaused: false,
    today: { [meId]: { reviews: 0, newWords: 0, studied: false } },
    record: { daysStudied: { [meId]: 0 }, daysTogether: 0, steadyWords: { [meId]: 0 } },
  };
}

/**
 * The pulled state may be hours old. Replace my own "today" and steady-word count with what this
 * device knows (local-first), keep the partner's numbers from the server.
 */
export function withLocalToday(
  shared: SharedState,
  opts: {
    meId: string;
    cards: readonly CardRow[];
    reviewedToday: number;
    newToday: number;
    pace: PaceConfig;
    now: Date;
    sharedAt?: string;
    /** Notebook tasks marked done today on this device: a study day even without a review. */
    notebookToday?: number;
  },
): SharedState {
  const { meId, now, pace } = opts;
  const steady = new Set(
    opts.cards
      .filter((c) => (c.type === 'hv-native' || c.type === 'native-hv') && ((c.state as StoredCard | undefined)?.stability ?? 0) >= STEADY_DAYS)
      .map((c) => c.itemId),
  ).size;
  const server = shared.today[meId] ?? { reviews: 0, newWords: 0, studied: false };
  // If the pulled state is from an earlier local day, its "today" is stale.
  const sameDay = opts.sharedAt ? localDate(new Date(opts.sharedAt), pace.timezone) === localDate(now, pace.timezone) : false;
  const reviews = Math.max(sameDay ? server.reviews : 0, opts.reviewedToday);
  const newWords = Math.max(sameDay ? server.newWords : 0, opts.newToday);
  // A day with only notebook tasks done is a study day too (the server's study_day row says so).
  const studied = reviews + newWords > 0 || (opts.notebookToday ?? 0) > 0 || (sameDay && server.studied);
  const today = { ...shared.today, [meId]: { reviews, newWords, studied } };
  if (!sameDay) {
    for (const id of Object.keys(today)) if (id !== meId) today[id] = { reviews: 0, newWords: 0, studied: false };
  }
  return {
    ...shared,
    today,
    record: {
      ...shared.record,
      steadyWords: { ...shared.record.steadyWords, [meId]: steady },
    },
  };
}

/**
 * The pace with the lessons opened early by a joint vote that the last sync brought
 * (SharedState.earlyOpens, 2026-10-07): every date then moves at once, before /api/me is read
 * again. Openings only ever add up, so this is a union by lesson. The same object when nothing is new.
 */
export function paceWithOpens(pace: PaceConfig, opens: readonly EarlyOpen[] | undefined): PaceConfig {
  if (!opens?.length) return pace;
  const have = pace.earlyOpens ?? [];
  const add = opens.filter((o) => !have.some((h) => h.lesson === o.lesson));
  if (!add.length) return pace;
  return { ...pace, earlyOpens: [...have, ...add].sort((a, b) => a.lesson - b.lesson) };
}

/**
 * The pace with the staircase's stairs reached that the last sync brought (SharedState.rises, the
 * rising word pace of 2026-10-14): the rate, the next rise and the step table then follow the server.
 * The same object when nothing changed, or for a flat pace.
 */
export function paceWithRises(pace: PaceConfig, rises: readonly PaceRise[] | undefined): PaceConfig {
  if (!pace.staircase || !rises) return pace;
  if (pace.rises && JSON.stringify(pace.rises) === JSON.stringify(rises)) return pace;
  return { ...pace, rises: [...rises] };
}

/** The pace with what the last sync knew: the lessons opened early and the stairs reached. */
export function paceFromShared(pace: PaceConfig, shared: Pick<SharedState, 'earlyOpens' | 'rises'> | null | undefined): PaceConfig {
  return paceWithRises(paceWithOpens(pace, shared?.earlyOpens), shared?.rises);
}
